import type { Character } from '@dcc/rules/domain/character.ts';
import type { DerivedStats } from '@dcc/rules/app/derivedStats.ts';

/**
 * The top of every sheet tab: the Health Bar, then Mana and AI Favor.
 *
 * Drawn after the printable DCC character sheet's Health Bar: ten boxes on a
 * red-to-green band, each labelled with the share of Health it stands for and
 * showing the slot value (the Con Mod), which is the damage it takes to mark
 * that box off (Rules p.42). A marked box gets the dot in its corner. Boxes are
 * marked from 100% downwards, so the 10% box is the last one standing.
 */
export function VitalsHeader({ character, derived }: { character: Character; derived: DerivedStats }) {
  const bar = derived.healthBar;
  const slots = Array.from({ length: bar.maxSlots }, (_, index) => ({
    index,
    percent: Math.round(((index + 1) / bar.maxSlots) * 100),
    marked: index >= bar.slotsRemaining,
  }));
  const rows = [slots.slice(0, 5), slots.slice(5)].filter((row) => row.length > 0);

  return (
    <section className="dcc-vitals-header" aria-label="Health, Mana and AI Favor">
      <div className="dcc-healthbar" aria-label={`Health Bar: ${bar.slotsRemaining} of ${bar.maxSlots} slots, ${bar.slotValue} Health per slot`}>
        {rows.map((row, r) => (
          <div key={r} className="dcc-healthbar-row">
            {row.map((slot) => (
              <div key={slot.index} className="dcc-healthbar-cell">
                <div className={slot.marked ? 'dcc-healthbox is-marked' : 'dcc-healthbox'} title={`${bar.slotValue} damage marks this box`}>
                  <span className="dcc-healthbox-value">{bar.slotValue}</span>
                  <span className="dcc-healthbox-dot" aria-hidden="true" />
                </div>
                <span className="dcc-healthbox-percent">{slot.percent}%</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className="dcc-health-summary">{bar.slotsRemaining}/{bar.maxSlots} slots · {Math.round(100 * bar.slotsRemaining / bar.maxSlots)}% · Slot threshold: {bar.slotValue} damage</p>
      <div className="dcc-vitals-row">
        <div className="dcc-vital">
          <span className="dcc-vital-label">Mana</span>
          <span className="dcc-vital-value">{character.currentMana}/{derived.mana.max}</span>
        </div>
        <div className="dcc-vital">
          <span className="dcc-vital-label">AI Favor</span>
          <span className="dcc-vital-value">{character.aiFavor}</span>
          <span className="dcc-favor-pips" aria-hidden="true">
            {Array.from({ length: Math.min(character.aiFavor, 10) }, (_, i) => <span key={i} className="dcc-favor-pip" />)}
          </span>
        </div>
      </div>
    </section>
  );
}
